import { createElement } from "react";
import { useController, useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Field, FieldDescription, FieldError } from "@fronterahealth/frontera-ui-components/ui/field";
import { Spinner } from "@fronterahealth/frontera-ui-components/ui/spinner";

import { ApiReportSectionFieldsFieldTypeChoices } from "@api/graphql/types-and-hooks";
import { getFieldAiGenerated } from "@components/FieldComponent/utils";
import { ACTIVE_REVIEW_FIELD_CLASSNAME, useFieldReview } from "@components/QaReview/hooks/useFieldReview";
import { useIsReviewPanelVisible } from "@components/QaReview/hooks/useIsReviewPanelVisible";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { ReportFieldLabel } from "@components/ReportField/ReportFieldLabel";
import { getReportFieldInput } from "@components/ReportField/registry";
import { ReportFieldProps, UpdateReportSectionFieldMutateOptions } from "@components/ReportField/types";
import { useReportFieldAutosave } from "@components/ReportField/useReportFieldAutosave";

export type { ReportFieldProps };

export const ReportField: React.FC<ReportFieldProps> = ({ section, field, variant, disabled }) => {
  const { field: rhfField, fieldState, formState } = useController<Record<string, string>>({ name: field.id });
  const { getValues, resetField } = useFormContext<Record<string, string>>();
  const { save, isPending } = useReportFieldAutosave(section, field);
  const { isActive, activate, deactivate } = useFieldReview();
  const { fieldRemountKeys, applyingFieldId } = useReviewSession().state;
  const isReviewPanelVisible = useIsReviewPanelVisible();
  const remountKey = fieldRemountKeys[field.id] ?? 0;

  const fieldInput = getReportFieldInput(field.fieldType, field.fieldIdentifier);
  if (!fieldInput) return null;

  const inputId = `${field.id}-input`;
  const isAi = getFieldAiGenerated(field);
  const value = typeof rhfField.value === "string" ? rhfField.value : field.content ?? "";
  const invalid = formState.isSubmitted && !!fieldState.error;

  const onChange = async (content: string, mutateOptions?: UpdateReportSectionFieldMutateOptions) => {
    rhfField.onChange(content);
    save(content, {
      ...mutateOptions,
      onSuccess: (data, ...rest) => {
        if (data.updateReportSectionField?.status && getValues(field.id) === content) {
          resetField(field.id, { defaultValue: content, keepError: true, keepTouched: true });
        }
        mutateOptions?.onSuccess?.(data, ...rest);
      },
    });
  };

  const variantFinal = isActive(field.id) ? "default" : variant;
  // Only beside a visible card: with the Outline showing, there's no card to pair with.
  const isFieldReviewed = isReviewPanelVisible && isActive(field.id);

  return (
    <Field
      id={field.id}
      className={cn(isFieldReviewed && ACTIVE_REVIEW_FIELD_CLASSNAME)}
      data-active-review-field={isFieldReviewed || undefined}
      data-testid={`${section.sectionIdentifier}.${field.fieldIdentifier}`}
      data-type={field.fieldType}
      data-invalid={invalid}
    >
      <ReportFieldLabel field={field} section={section} htmlFor={inputId} />
      {createElement(fieldInput, {
        key: remountKey,
        id: inputId,
        field,
        value,
        onChange,
        variant: variantFinal,
        placeholder: "--",
        className: twMerge(
          variantFinal !== "ghost" &&
            field.fieldType === ApiReportSectionFieldsFieldTypeChoices.Wysiwyg &&
            isAi &&
            "ai-gradient-border group-hover/field:ai-gradient-border-hover",
          variantFinal !== "ghost" &&
            field.fieldType !== ApiReportSectionFieldsFieldTypeChoices.Wysiwyg &&
            isAi &&
            "border-blurple-500 group-hover/field:border-blurple-500",
        ),
        disabled: disabled || applyingFieldId === field.id,
        onBlur: () => deactivate(field.id),
        onFocus: () => activate(field.id),
        ariaInvalid: invalid,
      })}
      <div className="relative -mt-2">
        <FieldDescription
          data-state={isPending ? "show" : "hide"}
          className={cn(
            "absolute flex gap-2 items-center right-0 top-2",
            "fade-in slide-in-from-top-2 fade-out slide-out-to-top-2 duration-100 fill-mode-forwards",
            "data-[state=show]:animate-in data-[state=hide]:animate-out",
          )}
        >
          <Spinner className="size-3 text-muted-foreground" />
        </FieldDescription>
      </div>
      {fieldState.error && <FieldError errors={[fieldState.error]} />}
    </Field>
  );
};
